/**
 * NewsFeedColumn — vertical Threads-style feed with cursor-pagination.
 *
 * Day-1 limit 50 cards/page. Click "Load more" → fetch next cursor (no
 * infinite scroll on Day-1 — explicit click avoids surprise data fetch
 * for low-bandwidth users + simpler analytics).
 *
 * Source: F-FE-HOTNEWS §3 + design source §2.4.
 */

import { useEffect, useState } from 'react';
import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';
import { NewsThreadCard, type NewsCardData } from './NewsThreadCard';
import { fetchNewsFeed, getCachedNewsFeed } from '../../lib/news-api';

export interface NewsFeedColumnProps {
  /** Optional pre-applied filter (topic / source / tag). */
  filter?: {
    topic?: string;
    source?: string;
    tag?: string;
  };
}

export function NewsFeedColumn({ filter }: NewsFeedColumnProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  // Seed from SWR cache so revisits render instantly; refresh in background.
  const cached = getCachedNewsFeed({ ...filter, limit: 50 });
  const [items, setItems] = useState<NewsCardData[]>(cached?.items ?? []);
  const [cursor, setCursor] = useState<string | null>(cached?.next_cursor ?? null);
  const [loading, setLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(Boolean(cached?.next_cursor));

  // Reset when filter changes
  useEffect(() => {
    let cancelled = false;
    const seed = getCachedNewsFeed({ ...filter, limit: 50 });
    if (seed) {
      setItems(seed.items);
      setCursor(seed.next_cursor ?? null);
      setHasMore(Boolean(seed.next_cursor));
      setLoading(false);
    } else {
      setLoading(true);
      setItems([]);
      setCursor(null);
    }
    setError(null);
    fetchNewsFeed({ ...filter, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setCursor(res.next_cursor ?? null);
        setHasMore(Boolean(res.next_cursor));
      })
      .catch((e: Error) => {
        if (!cancelled && !seed) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter?.topic, filter?.source, filter?.tag]);

  async function loadMore() {
    if (!cursor || loading) return;
    setLoading(true);
    try {
      const res = await fetchNewsFeed({ ...filter, limit: 50, cursor });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor ?? null);
      setHasMore(Boolean(res.next_cursor));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (loading && items.length === 0) {
    return <SkeletonFeed />;
  }

  if (error && items.length === 0) {
    return (
      <div role="alert" className="banner banner-warning" style={{ marginTop: 24 }}>
        {copy.error_cache_timeout}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="banner" style={{ marginTop: 24 }}>
        {filter?.topic
          ? copy.empty_topic_filter.replace('{topic}', filter.topic)
          : filter?.tag
            ? copy.empty_tag_filter.replace('{tag}', filter.tag)
            : filter?.source
              ? copy.empty_source_filter.replace('{source}', filter.source)
              : copy.empty_feed_cold}
      </div>
    );
  }

  return (
    <div role="feed" aria-busy={loading} aria-label={copy.feed_header_title}>
      {items.map((item) => (
        <NewsThreadCard key={item.id} news={item} />
      ))}
      {hasMore ? (
        <button
          type="button"
          onClick={loadMore}
          className="cta-secondary"
          style={{ display: 'block', margin: '24px auto', minWidth: 200 }}
          disabled={loading}
        >
          {loading ? '…' : (lang === 'en' ? 'Load more' : 'Tải thêm')}
        </button>
      ) : null}
    </div>
  );
}

function SkeletonFeed() {
  return (
    <div aria-hidden style={{ marginTop: 16 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            background: 'var(--asklaw-paper)',
            border: '1px solid var(--asklaw-border)',
            borderRadius: 14,
            padding: 20,
            marginBottom: 14,
            minHeight: 200,
          }}
        >
          <div style={skelLineStyle(140, 12)} />
          <div style={{ ...skelLineStyle(280, 18), marginTop: 12 }} />
          <div style={{ ...skelLineStyle(320, 14), marginTop: 8 }} />
          <div style={{ ...skelLineStyle(220, 14), marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}

function skelLineStyle(w: number, h: number): React.CSSProperties {
  return {
    width: `min(100%, ${w}px)`,
    height: h,
    background: 'var(--asklaw-cream-deep)',
    borderRadius: 4,
  };
}
